import React, { useState, useEffect, useCallback } from 'react';

const parseApiResponse = async (response) => {
  const text = await response.text();
  try { return JSON.parse(text); }
  catch { return { status: text }; }
};

const defaultFetchOptions = {
  credentials: 'include',
  headers: { 'accept': '*/*' }
};

const FormApiService = {
    API_BASE_URL :  'https://apps.genus.in/GenOpsAPI/api',
  //API_BASE_URL: 'http://localhost:5134/api',



  async getPCBLiveProjects(serverId, forDate) {
    const r = await fetch(`${this.API_BASE_URL}/PCB/live-projects/daily?serverId=${serverId}&forDate=${forDate}`, { ...defaultFetchOptions });
    if (!r.ok) throw new Error('Failed to fetch PCB projects');
    return parseApiResponse(r);
  },
  async getPCBRunningLines(serverId, projCode, forDate) {
    const r = await fetch(`${this.API_BASE_URL}/PCB/running-lines/daily?serverId=${serverId}&projCode=${projCode}&forDate=${forDate}`, { ...defaultFetchOptions });
    if (!r.ok) throw new Error('Failed to fetch PCB running lines');
    return parseApiResponse(r);
  },
  async getEMALiveProjects(serverId) {
    const r = await fetch(`${this.API_BASE_URL}/Setting/LiveProjectList?serverID=${serverId}`, { ...defaultFetchOptions });
    if (!r.ok) throw new Error('Failed to fetch EMA projects');
    return parseApiResponse(r);
  },
  async getEMARunningLines(serverId, projCode, forDate) {
    const r = await fetch(`${this.API_BASE_URL}/Setting/RunningLinesDaily?serverId=${serverId}&projCode=${projCode}&forDate=${forDate}`, { ...defaultFetchOptions });
    if (!r.ok) throw new Error('Failed to fetch EMA running lines');
    return parseApiResponse(r);
  },
  async getProductionGaps(serverId, projCode, lineNumber, shift, forDate, area, minGapMinutes = 5) {
    const params = new URLSearchParams({ serverId, projCode, lineNumber, shift, forDate, area, minGapMinutes });
    const r = await fetch(`${this.API_BASE_URL}/EntryFrom/gaps/daily?${params}`, { ...defaultFetchOptions });
    if (!r.ok) throw new Error('Failed to fetch production gaps');
    return parseApiResponse(r);
  },
  async saveProductionHeader(requestData) {
    const r = await fetch(`${this.API_BASE_URL}/EntryFrom/production/save`, {
      method: 'POST', credentials: 'include',
      headers: { accept: '*/*', 'Content-Type': 'application/json' },
      body: JSON.stringify(requestData),
    });
    if (!r.ok) throw new Error('Failed to save production header');
    return parseApiResponse(r);
  },
  // Returns the full saved header ({productionId, target, shiftTimingId,
  // meterInfo}) or null if nothing saved yet for this exact combination.
  async getProductionId(serverId, prodDate, shift, area, projectCode, lineNo) {
    const params = new URLSearchParams({ serverId, prodDate, shift, area, projectCode, lineNo });
    const r = await fetch(`${this.API_BASE_URL}/EntryFrom/production/id?${params}`, { ...defaultFetchOptions });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error('Failed to get production ID');
    return parseApiResponse(r);
  },
  async saveDowntimeEntries(serverId, productionId, entries) {
    const params = new URLSearchParams({ serverId, productionId });
    const r = await fetch(`${this.API_BASE_URL}/EntryFrom/downtime/save?${params}`, {
      method: 'POST', credentials: 'include',
      headers: { accept: '*/*', 'Content-Type': 'application/json' },
      body: JSON.stringify(entries),
    });
    if (!r.ok) {
      // ✅ Read the actual backend error text instead of throwing a generic
      // message — the API returns the real SQL/exception text in the body.
      const bodyText = await r.text().catch(() => '');
      throw new Error(bodyText || `Failed to save downtime entries (HTTP ${r.status})`);
    }
    return parseApiResponse(r);
  },
  async getProjectPhase(serverId, projCode) {
    const r = await fetch(`${this.API_BASE_URL}/ProjectPhase/phase?serverId=${serverId}&projCode=${projCode}`, { ...defaultFetchOptions });
    if (!r.ok) throw new Error('Failed to fetch project phase');
    return parseApiResponse(r);
  },
  async getSavedDowntimeEntries(serverId, prodDate, shift, area, projectCode, lineNo) {
    const params = new URLSearchParams({ serverId, prodDate, shift, area, projectCode, lineNo });
    const r = await fetch(`${this.API_BASE_URL}/EntryFrom/savedproductiondetails/daily?${params}`, { ...defaultFetchOptions });
    if (!r.ok) return [];
    const data = await r.json();
    if (Array.isArray(data) && data.length > 0) return data[0].downtimes || [];
    return [];
  },
  async getShiftTimings(serverId, shift) {
    const params = new URLSearchParams({ serverId });
    if (shift) params.append('shift', shift);
    const r = await fetch(`${this.API_BASE_URL}/EntryFrom/shifttimings?${params}`, { ...defaultFetchOptions });
    if (!r.ok) throw new Error('Failed to fetch shift timings');
    return parseApiResponse(r);
  },
  async getPreviousTarget(serverId, area, projCode, lineNo, beforeDate) {
    const params = new URLSearchParams({ serverId, area, projCode, lineNo, beforeDate });
    const r = await fetch(`${this.API_BASE_URL}/EntryFrom/previoustarget?${params}`, { ...defaultFetchOptions });
    if (!r.ok) return null;
    const data = await r.json();
    return data?.target ?? null;
  },
  // ✅ NEW — mirrors getPreviousTarget for shift timing auto-fill
  async getPreviousShiftTiming(serverId, area, projCode, lineNo, beforeDate) {
    const params = new URLSearchParams({ serverId, area, projCode, lineNo, beforeDate });
    const r = await fetch(`${this.API_BASE_URL}/EntryFrom/previousshifttiming?${params}`, { ...defaultFetchOptions });
    if (!r.ok) return null;
    return parseApiResponse(r);
  },
  async getRecentDowntimeReasons(serverId, area, searchTerm = '') {
    const params = new URLSearchParams({ serverId, area });
    if (searchTerm) params.append('searchTerm', searchTerm);
    const r = await fetch(`${this.API_BASE_URL}/EntryFrom/downtimereasons?${params}`, { ...defaultFetchOptions });
    if (!r.ok) return [];
    return parseApiResponse(r);
  },
};


const DowntimeForm = () => {
  const [formData, setFormData] = useState({
    plant: '2',
    date: new Date().toISOString().split('T')[0],
    shift: 'A',
    area: 'PCB',
    project: '',
    line: '',
    meterInfo: '',
    target: '',
    shiftTimingId: '',       // ✅ per-line timing
  });

  const [shiftTimings, setShiftTimings] = useState([]);
  const [productionId,     setProductionId]     = useState(null);
  const [productionSaved,  setProductionSaved]  = useState(false);
  // ✅ NEW — holds the ShiftTimingId actually saved on TabGenOpsDesc for
  // this exact (date, shift, area, project, line), so it can be restored
  // into the dropdown once the shiftTimings list has loaded. This is
  // separate from the "previous day" auto-fill, which intentionally only
  // looks at earlier dates and would never find today's own saved value.
  const [savedHeaderShiftTimingId, setSavedHeaderShiftTimingId] = useState(null);
  const [downtimeEntries,  setDowntimeEntries]  = useState([]);
  const [timeFrameGaps,    setTimeFrameGaps]    = useState([]);
  const [savedEntriesLoaded, setSavedEntriesLoaded] = useState(false);

  const [currentEntry, setCurrentEntry] = useState({
    time: '', gapMinutes: '', category: '', reason: '',
  });

  const [reasonSuggestions, setReasonSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [projectList, setProjectList] = useState([]);
  const [lineList,    setLineList]    = useState([]);
  const [loading, setLoading] = useState({
    projects: false, lines: false, timeFrames: false,
    savingProduction: false, checkingProduction: false,
    savingDowntime: false, phaseType: false, loadingEntries: false,
    shiftTimings: false,
  });

  const ServerInfo = [
    { id: 2, name: 'RCP Jaipur' },
    { id: 3, name: 'HDR 1101' },
    { id: 4, name: 'HDR 3000' },
    { id: 5, name: 'HDR 1100' },
    { id: 6, name: 'Guwahati' },
  ];
  const shiftOptions    = ['A', 'B'];
  const areaOptions     = ['PCB', 'EMA'];
  const categoryOptions = ['Planned', 'Unplanned'];

  const getDisplayValue = (item, ...keys) => {
    if (typeof item === 'string' || typeof item === 'number') return String(item);
    if (typeof item === 'object' && item !== null) {
      for (const k of keys) if (item[k] !== undefined && item[k] !== null) return String(item[k]);
    }
    return '';
  };
  const getValueForSubmission = getDisplayValue;

  // ✅ Fetch shift timings only after the line is selected
  useEffect(() => {
    const fetchTimings = async () => {
      if (!formData.plant || !formData.shift || !formData.line) {
        setShiftTimings([]); return;
      }
      setLoading(p => ({ ...p, shiftTimings: true }));
      try {
        const timings = await FormApiService.getShiftTimings(
          parseInt(formData.plant), formData.shift);
        const arr = Array.isArray(timings) ? timings : [];
        setShiftTimings(arr);
        if (arr.length > 0) {
          setFormData(prev => ({
            ...prev,
            shiftTimingId: prev.shiftTimingId && arr.some(t => t.id === parseInt(prev.shiftTimingId))
              ? prev.shiftTimingId : '',
          }));
        } else {
          setFormData(prev => ({ ...prev, shiftTimingId: '' }));
        }
      } catch (err) {
        console.error('Shift timings fetch failed:', err);
        setShiftTimings([]);
      } finally {
        setLoading(p => ({ ...p, shiftTimings: false }));
      }
    };
    fetchTimings();
  }, [formData.plant, formData.shift, formData.line]);

  // ✅ Auto-fill previous target when project+line selected
  useEffect(() => {
    const autoFill = async () => {
      if (!formData.plant || !formData.area || !formData.project || !formData.line) return;
      try {
        const prev = await FormApiService.getPreviousTarget(
          parseInt(formData.plant), formData.area,
          formData.project, parseInt(formData.line), formData.date);
        if (prev && !formData.target) {
          setFormData(p => ({ ...p, target: String(prev) }));
        }
      } catch { /* silent */ }
    };
    autoFill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.plant, formData.area, formData.project, formData.line]);

  // ✅ NEW — Auto-fill previous shift timing when project+line selected,
  // same UX pattern as the target auto-fill above. Only fills if the
  // user hasn't already picked a timing, and only if that timing still
  // exists in the currently loaded list for this shift.
  useEffect(() => {
    const autoFillTiming = async () => {
      if (!formData.plant || !formData.area || !formData.project || !formData.line) return;
      if (shiftTimings.length === 0) return;
      if (formData.shiftTimingId) return; // don't override a user's current selection
      try {
        const prev = await FormApiService.getPreviousShiftTiming(
          parseInt(formData.plant), formData.area,
          formData.project, parseInt(formData.line), formData.date);
        const prevId = prev?.shiftTimingId;
        if (prevId && shiftTimings.some(t => t.id === prevId)) {
          setFormData(p => (p.shiftTimingId ? p : { ...p, shiftTimingId: String(prevId) }));
        }
      } catch { /* silent */ }
    };
    autoFillTiming();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.plant, formData.area, formData.project, formData.line, shiftTimings]);

  // ✅ NEW — applies the ACTUAL saved shift timing for today's own record
  // (from savedHeaderShiftTimingId) once the shiftTimings list for this
  // plant+shift has loaded and contains that id. Runs independently of
  // the "previous day" auto-fill above and takes priority over it, since
  // that effect only fires when shiftTimingId is still empty — so once
  // this effect sets it, the previous-day guess is correctly skipped.
  useEffect(() => {
    if (!savedHeaderShiftTimingId) return;
    if (shiftTimings.length === 0) return;
    if (!shiftTimings.some(t => t.id === savedHeaderShiftTimingId)) return;
    setFormData(p => (
      p.shiftTimingId === String(savedHeaderShiftTimingId)
        ? p
        : { ...p, shiftTimingId: String(savedHeaderShiftTimingId) }
    ));
  }, [savedHeaderShiftTimingId, shiftTimings]);

  const fetchReasonSuggestions = async (searchTerm = '') => {
    if (!formData.plant || !formData.area) return;
    try {
      const data = await FormApiService.getRecentDowntimeReasons(
        parseInt(formData.plant), formData.area, searchTerm);
      setReasonSuggestions(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  };

  // Existing loaders (unchanged)
  const loadSavedDowntimeEntries = useCallback(async (
    serverId, prodDate, shift, area, projectCode, lineNo) => {
    setLoading(p => ({ ...p, loadingEntries: true }));
    try {
      const entries = await FormApiService.getSavedDowntimeEntries(
        serverId, prodDate, shift, area, projectCode, lineNo);
      if (entries.length > 0) {
        setDowntimeEntries(entries.map((e, i) => ({
          id: Date.now() + i,
          time: `${e.gapStartTime} - ${e.gapEndTime}`,
          gapStartTime: e.gapStartTime, gapEndTime: e.gapEndTime,
          gapMinutes: e.gapMinutes || '', category: e.category || '',
          reason: e.reason || '', fromServer: true,
        })));
      } else setDowntimeEntries([]);
      setSavedEntriesLoaded(true);
    } catch {
      setDowntimeEntries([]); setSavedEntriesLoaded(true);
    } finally { setLoading(p => ({ ...p, loadingEntries: false })); }
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!formData.plant || !formData.date || !formData.shift || !formData.area) {
        setProjectList([]); return;
      }
      setLoading(p => ({ ...p, projects: true }));
      try {
        const sid = parseInt(formData.plant);
        let projects = [];
        if (formData.area === 'PCB') projects = await FormApiService.getPCBLiveProjects(sid, formData.date);
        else if (formData.area === 'EMA') projects = await FormApiService.getEMALiveProjects(sid);
        setProjectList(Array.isArray(projects) ? projects : []);
        setFormData(p => ({ ...p, project: '', line: '', meterInfo: '', target: '', shiftTimingId: '' }));
        setLineList([]); setTimeFrameGaps([]);
        setProductionId(null); setProductionSaved(false);
        setSavedHeaderShiftTimingId(null);
        setDowntimeEntries([]); setSavedEntriesLoaded(false);
      } catch (err) { alert(err.message); setProjectList([]); }
      finally { setLoading(p => ({ ...p, projects: false })); }
    };
    fetchProjects();
  }, [formData.plant, formData.date, formData.shift, formData.area]);

  useEffect(() => {
    const fetchLines = async () => {
      if (!formData.project || !formData.area) { setLineList([]); return; }
      setLoading(p => ({ ...p, lines: true }));
      try {
        const sid = parseInt(formData.plant);
        let lines = [];
        if (formData.area === 'PCB') lines = await FormApiService.getPCBRunningLines(sid, formData.project, formData.date);
        else if (formData.area === 'EMA') lines = await FormApiService.getEMARunningLines(sid, formData.project, formData.date);
        setLineList(Array.isArray(lines) ? lines : []);
        setFormData(p => ({ ...p, line: '', shiftTimingId: '', target: '' }));
        setTimeFrameGaps([]); setProductionId(null);
        setProductionSaved(false); setDowntimeEntries([]);
        setSavedHeaderShiftTimingId(null);
        setSavedEntriesLoaded(false);
      } catch (err) { alert(err.message); setLineList([]); }
      finally { setLoading(p => ({ ...p, lines: false })); }
    };
    fetchLines();
  }, [formData.project, formData.area, formData.plant, formData.date]);

  useEffect(() => {
    const fetchPhase = async () => {
      if (!formData.plant || !formData.project || !formData.area) return;
      setLoading(p => ({ ...p, phaseType: true }));
      try {
        const d = await FormApiService.getProjectPhase(parseInt(formData.plant), formData.project);
        setFormData(prev => ({ ...prev, meterInfo: d?.phaseType || '' }));
      } catch { setFormData(prev => ({ ...prev, meterInfo: '' })); }
      finally { setLoading(p => ({ ...p, phaseType: false })); }
    };
    fetchPhase();
  }, [formData.plant, formData.project, formData.area]);

  useEffect(() => {
    const check = async () => {
      if (!formData.plant || !formData.date || !formData.shift || !formData.area ||
          !formData.project || !formData.line) {
        setProductionId(null); setProductionSaved(false);
        setSavedHeaderShiftTimingId(null);
        setDowntimeEntries([]); setSavedEntriesLoaded(false);
        return;
      }
      setLoading(p => ({ ...p, checkingProduction: true }));
      try {
        const sid = parseInt(formData.plant), lno = parseInt(formData.line);
        const header = await FormApiService.getProductionId(sid, formData.date, formData.shift, formData.area, formData.project, lno);
        if (header) {
          setProductionId(header.productionId); setProductionSaved(true);
          // ✅ NEW — restore what was ACTUALLY saved for this exact date,
          // rather than leaving the form to guess from a prior day.
          if (header.target != null) {
            setFormData(p => ({ ...p, target: String(header.target) }));
          }
          setSavedHeaderShiftTimingId(header.shiftTimingId ?? null);
          await loadSavedDowntimeEntries(sid, formData.date, formData.shift, formData.area, formData.project, lno);
        } else {
          setProductionId(null); setProductionSaved(false);
          setSavedHeaderShiftTimingId(null);
          setDowntimeEntries([]); setSavedEntriesLoaded(false);
        }
      } catch { setProductionId(null); setProductionSaved(false); }
      finally { setLoading(p => ({ ...p, checkingProduction: false })); }
    };
    check();
  }, [formData.plant, formData.date, formData.shift, formData.area,
      formData.project, formData.line, loadSavedDowntimeEntries]);

  useEffect(() => {
    const gaps = async () => {
      if (!formData.plant || !formData.date || !formData.shift || !formData.area ||
          !formData.project || !formData.line || !productionId) {
        setTimeFrameGaps([]); return;
      }
      setLoading(p => ({ ...p, timeFrames: true }));
      try {
        const g = await FormApiService.getProductionGaps(
          parseInt(formData.plant), formData.project, parseInt(formData.line),
          formData.shift, formData.date, formData.area, 5);
        setTimeFrameGaps(Array.isArray(g) ? g : []);
      } catch { setTimeFrameGaps([]); }
      finally { setLoading(p => ({ ...p, timeFrames: false })); }
    };
    gaps();
  }, [formData.plant, formData.date, formData.shift, formData.area,
      formData.project, formData.line, productionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEntryChange = (e) => {
    const { name, value } = e.target;
    if (name === 'time') {
      const g = timeFrameGaps.find(x => x.timeRange === value);
      setCurrentEntry(p => ({ ...p, [name]: value, gapMinutes: g ? g.gapMinutes : '' }));
    } else if (name === 'reason') {
      setCurrentEntry(p => ({ ...p, [name]: value }));
      if (value.length >= 1) { fetchReasonSuggestions(value); setShowSuggestions(true); }
      else setShowSuggestions(false);
    } else setCurrentEntry(p => ({ ...p, [name]: value }));
  };

  const validateProductionParams = () => {
    if (!formData.plant) return 'Please select a plant';
    if (!formData.date) return 'Please select a date';
    if (!formData.shift) return 'Please select a shift';
    if (!formData.area) return 'Please select an area';
    if (!formData.project) return 'Please select a project';
    if (!formData.line) return 'Please select a line';
    if (!formData.shiftTimingId) return 'Please select a shift timing';
    if (!formData.meterInfo) return 'Meter info not detected';
    if (!formData.target) return 'Please enter a target';
    if (parseInt(formData.target) <= 0) return 'Target must be greater than 0';
    return null;
  };

  const saveProductionParameters = async () => {
    const err = validateProductionParams();
    if (err) { alert(err); return; }
    setLoading(p => ({ ...p, savingProduction: true }));
    try {
      const requestData = {
        serverId: parseInt(formData.plant),
        prodDate: formData.date,
        shift: formData.shift,
        area: formData.area,
        projectCode: formData.project,
        lineNo: parseInt(formData.line),
        meterInfo: formData.meterInfo,
        target: parseInt(formData.target),
        shiftTimingId: parseInt(formData.shiftTimingId),
      };
      const result = await FormApiService.saveProductionHeader(requestData);
      setProductionId(result.productionId);
      setProductionSaved(true);
      const timing = shiftTimings.find(t => t.id === parseInt(formData.shiftTimingId));
      alert(`✓ Production Parameters Saved!\n\nID: ${result.productionId}\nTiming: ${timing?.name || ''}\nTarget: ${formData.target}`);
    } catch (e) { alert(`Error: ${e.message}`); }
    finally { setLoading(p => ({ ...p, savingProduction: false })); }
  };

  const resetProductionParameters = () => {
    if (!window.confirm('Reset all production parameters?')) return;
    setFormData({
      plant: '', date: new Date().toISOString().split('T')[0],
      shift: '', area: '', project: '', line: '',
      meterInfo: '', target: '', shiftTimingId: '',
    });
    setProductionId(null); setProductionSaved(false);
    setSavedHeaderShiftTimingId(null);
    setDowntimeEntries([]); setSavedEntriesLoaded(false);
    setTimeFrameGaps([]);
    setCurrentEntry({ time: '', gapMinutes: '', category: '', reason: '' });
    setProjectList([]); setLineList([]); setShiftTimings([]);
  };

  const addDowntimeEntry = () => {
    if (!currentEntry.time || !currentEntry.category || !currentEntry.reason) {
      alert('Please fill all downtime entry fields'); return;
    }
    if (!currentEntry.gapMinutes) { alert('Gap minutes not available'); return; }
    const [s, e] = currentEntry.time.split(' - ');
    setDowntimeEntries([...downtimeEntries, {
      ...currentEntry, id: Date.now(), gapStartTime: s, gapEndTime: e, fromServer: false,
    }]);
    setCurrentEntry({ time: '', gapMinutes: '', category: '', reason: '' });
  };

  const deleteEntry = (id) => setDowntimeEntries(downtimeEntries.filter(e => e.id !== id));

  const saveDowntimeEntries = async () => {
    if (!productionId) return alert('Production ID not found');
    if (downtimeEntries.length === 0) return alert('Please add at least one entry');
    setLoading(p => ({ ...p, savingDowntime: true }));
    try {
      const entries = downtimeEntries.map(e => ({
        gapStartTime: e.gapStartTime, gapEndTime: e.gapEndTime,
        category: e.category, reason: e.reason,
      }));
      const result = await FormApiService.saveDowntimeEntries(
        parseInt(formData.plant), productionId, entries);
      alert(`✓ Saved ${entries.length} entries`);
      await loadSavedDowntimeEntries(parseInt(formData.plant), formData.date,
        formData.shift, formData.area, formData.project, parseInt(formData.line));
      setCurrentEntry({ time: '', gapMinutes: '', category: '', reason: '' });
    } catch (e) { alert(`Error: ${e.message}`); }
    finally { setLoading(p => ({ ...p, savingDowntime: false })); }
  };

  const resetDowntimeGrid = () => {
    if (!window.confirm('Reset all downtime entries?')) return;
    setDowntimeEntries([]); setSavedEntriesLoaded(false);
    setCurrentEntry({ time: '', gapMinutes: '', category: '', reason: '' });
  };

  const hasServerEntries = savedEntriesLoaded && downtimeEntries.some(e => e.fromServer);

  // ═════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Production Downtime Entry Form</h1>
        <p style={styles.subtitle}>
          <span style={styles.stepBadge}>Step 1</span> Save Production Parameters •{' '}
          <span style={styles.stepBadge}>Step 2</span> Record Downtime
        </p>
      </div>

      <div style={styles.formWrapper}>
        {/* Production Parameters */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionIcon}>🏭</span>
            Production Parameters
            <span style={styles.badge}>Morning Shift</span>
            {productionSaved && productionId && (
              <span style={styles.savedBadge}>✓ Saved • ID: {productionId}</span>
            )}
            {loading.checkingProduction && (
              <span style={styles.checkingBadge}>⏳ Checking...</span>
            )}
          </h2>

          {productionSaved && productionId && (
            <div style={styles.successBanner}>
              ✓ Production parameters saved. You can update them or add downtime entries.
            </div>
          )}

          {/* ✅ 5-4 split: row 1 = 5 columns, row 2 = 4 columns */}
          <div style={styles.grid5}>
            {/* 1. Plant */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Plant <span style={styles.required}>*</span></label>
              <select name="plant" value={formData.plant} onChange={handleInputChange}
                style={styles.select} disabled={loading.savingProduction}>
                <option value="">Select plant</option>
                {ServerInfo.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            {/* 2. Date */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Date <span style={styles.required}>*</span></label>
              <input type="date" name="date" value={formData.date} onChange={handleInputChange}
                max={new Date().toISOString().split('T')[0]} style={styles.input}
                disabled={loading.savingProduction} />
            </div>

            {/* 3. Shift */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Shift <span style={styles.required}>*</span></label>
              <select name="shift" value={formData.shift} onChange={handleInputChange}
                style={styles.select} disabled={loading.savingProduction}>
                <option value="">Select shift</option>
                {shiftOptions.map(o => <option key={o} value={o}>Shift {o}</option>)}
              </select>
            </div>

            {/* 4. Area */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Area <span style={styles.required}>*</span></label>
              <select name="area" value={formData.area} onChange={handleInputChange}
                style={styles.select} disabled={loading.savingProduction}>
                <option value="">Select area</option>
                {areaOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* 5. Project */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Project <span style={styles.required}>*</span></label>
              <select name="project" value={formData.project} onChange={handleInputChange}
                style={styles.select}
                disabled={loading.projects || projectList.length === 0 || loading.savingProduction}>
                <option value="">
                  {loading.projects ? 'Loading...' :
                   projectList.length === 0 ? 'Fill plant/date/shift/area first' :
                   'Select project'}
                </option>
                {projectList.map((p, i) => (
                  <option key={i} value={getValueForSubmission(p, 'projCode', 'projectCode', 'code', 'id')}>
                    {getDisplayValue(p, 'projName', 'projectName', 'name', 'projCode', 'projectCode') || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            {/* 6. Line No */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Line No <span style={styles.required}>*</span></label>
              <select name="line" value={formData.line} onChange={handleInputChange}
                style={styles.select}
                disabled={loading.lines || lineList.length === 0 || loading.savingProduction}>
                <option value="">
                  {loading.lines ? 'Loading...' :
                   lineList.length === 0 ? 'Select project first' :
                   'Select line'}
                </option>
                {lineList.map((l, i) => (
                  <option key={i} value={getValueForSubmission(l, 'lineNo', 'lineName', 'line', 'name', 'userId', 'id')}>
                    {getDisplayValue(l, 'lineNo', 'lineName', 'line', 'name', 'userName', 'userId') || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>

            {/* 7. ✅ Shift Timing — appears after line selected, loads for that line's shift */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Shift Timing <span style={styles.required}>*</span></label>
              <select name="shiftTimingId" value={formData.shiftTimingId} onChange={handleInputChange}
                style={styles.select}
                disabled={loading.savingProduction || !formData.line || loading.shiftTimings || shiftTimings.length === 0}>
                <option value="">
                  {!formData.line ? 'Select line first' :
                   loading.shiftTimings ? 'Loading...' :
                   shiftTimings.length === 0 ? 'No timings for this shift' :
                   'Select timing'}
                </option>
                {shiftTimings.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* 8. Meter Info */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Meter Info <span style={styles.required}>*</span></label>
              <input type="text" value={formData.meterInfo} readOnly
                placeholder={loading.phaseType ? 'Detecting...' : 'Auto-detected'}
                style={{ ...styles.input,
                         backgroundColor: formData.meterInfo ? '#e0f2fe' : '#f1f5f9',
                         cursor: 'not-allowed' }} disabled />
            </div>

            {/* 9. Target */}
            <div style={styles.inputGroup}>
              <label style={styles.label}>Target/Shift <span style={styles.required}>*</span></label>
              <input type="number" name="target" value={formData.target}
                onChange={handleInputChange} placeholder="Enter target"
                style={styles.input} min="1" disabled={loading.savingProduction} />
            </div>
          </div>

          <div style={styles.actionButtons}>
            <button onClick={saveProductionParameters} style={styles.saveButton}
              disabled={loading.savingProduction}>
              {loading.savingProduction ? '⏳ Saving...' :
               productionSaved ? '💾 Update Parameters' : '💾 Save Parameters'}
            </button>
            <button onClick={resetProductionParameters} style={styles.resetButton}
              disabled={loading.savingProduction}>🔄 Reset</button>
          </div>
        </div>

        {/* Downtime Grid — unchanged except for reason suggestions */}
        <div style={{ ...styles.section, opacity: !productionId ? 0.6 : 1 }}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.sectionIcon}>⏱️</span>
            Downtime Grid
            <span style={styles.badge}>Evening Shift</span>
            {productionId && <span style={styles.savedBadge}>✓ Production ID: {productionId}</span>}
          </h2>

          {!productionId && (
            <div style={styles.warningBanner}>⚠️ Save production parameters first.</div>
          )}

          {productionId && loading.loadingEntries && (
            <div style={styles.loadingBanner}>⏳ Loading saved entries...</div>
          )}
          {productionId && loading.timeFrames && !loading.loadingEntries && (
            <div style={styles.loadingBanner}>⏳ Loading production gaps...</div>
          )}
          {productionId && !loading.loadingEntries && hasServerEntries && (
            <div style={{ ...styles.infoBanner, background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)' }}>
              ℹ️ Previously saved entries loaded below.
            </div>
          )}
          {productionId && !loading.loadingEntries && !loading.timeFrames && timeFrameGaps.length === 0 && (
            <div style={styles.infoBanner}>ℹ️ No production gaps found.</div>
          )}

          <div style={{ ...styles.entryForm, pointerEvents: !productionId ? 'none' : 'auto' }}>
            <div style={styles.entryGrid}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Time Range</label>
                <select name="time" value={currentEntry.time} onChange={handleEntryChange}
                  style={styles.select} disabled={!productionId || timeFrameGaps.length === 0}>
                  <option value="">
                    {!productionId ? 'Save parameters first' :
                     timeFrameGaps.length === 0 ? 'No gaps available' :
                     'Select time range'}
                  </option>
                  {timeFrameGaps.map((g, i) => (
                    <option key={i} value={g.timeRange}>
                      {g.timeRange} ({parseFloat(g.gapMinutes).toFixed(2)} min)
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Gap (Minutes)</label>
                <input type="text" value={currentEntry.gapMinutes ? parseFloat(currentEntry.gapMinutes).toFixed(2) : ''}
                  readOnly placeholder="Auto-filled"
                  style={{ ...styles.input, backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
                  disabled={!productionId} />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Category</label>
                <select name="category" value={currentEntry.category} onChange={handleEntryChange}
                  style={styles.select} disabled={!productionId}>
                  <option value="">Select category</option>
                  {categoryOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div style={{ ...styles.inputGroup, position: 'relative' }}>
                <label style={styles.label}>Reason</label>
                <input type="text" name="reason" value={currentEntry.reason}
                  onChange={handleEntryChange}
                  onFocus={() => { if (!currentEntry.reason) { fetchReasonSuggestions(''); setShowSuggestions(true); } }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Type or pick a previous reason"
                  style={styles.input} disabled={!productionId} autoComplete="off" />
                {showSuggestions && reasonSuggestions.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                    background: 'white', border: '2px solid #e2e8f0', borderRadius: 10,
                    maxHeight: 220, overflowY: 'auto', zIndex: 50,
                    boxShadow: '0 8px 20px rgba(0,0,0,0.12)'
                  }}>
                    {reasonSuggestions.map((s, i) => (
                      <div key={i}
                        onMouseDown={(ev) => {
                          ev.preventDefault();
                          setCurrentEntry(prev => ({
                            ...prev, reason: s.reason, category: s.category || prev.category,
                          }));
                          setShowSuggestions(false);
                        }}
                        style={{
                          padding: '9px 12px', cursor: 'pointer', display: 'flex',
                          alignItems: 'center', gap: 10, borderBottom: '1px solid #f1f5f9', fontSize: 13,
                        }}
                        onMouseEnter={(ev) => ev.currentTarget.style.background = '#eff6ff'}
                        onMouseLeave={(ev) => ev.currentTarget.style.background = 'white'}>
                        <span style={{
                          fontSize: 10, padding: '2px 8px', borderRadius: 10,
                          background: s.category === 'Planned' ? '#fef3c7' : '#fee2e2',
                          color: s.category === 'Planned' ? '#92400e' : '#991b1b', fontWeight: 700,
                        }}>{s.category}</span>
                        <span style={{ flex: 1, color: '#1e293b' }}>{s.reason}</span>
                        <span style={{ fontSize: 10, color: '#94a3b8' }}>used {s.usageCount}×</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={styles.buttonWrapper}>
                <button onClick={addDowntimeEntry} style={styles.addButton} disabled={!productionId}>
                  ➕ Add Entry
                </button>
              </div>
            </div>
          </div>

          {downtimeEntries.length > 0 && (
            <div style={styles.tableContainer}>
              <div style={styles.tableHeader}>
                <span style={styles.tableHeaderText}>
                  📊 Entries ({downtimeEntries.length})
                  {hasServerEntries && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#0891b2', fontWeight: 600 }}>
                      (loaded from server)
                    </span>
                  )}
                </span>
                <span style={styles.tableHeaderText}>
                  Total Gap: {downtimeEntries.reduce((s, e) => s + parseFloat(e.gapMinutes || 0), 0).toFixed(2)} min
                </span>
              </div>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Time Range</th>
                    <th style={styles.th}>Gap (Min)</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Reason</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {downtimeEntries.map(e => (
                    <tr key={e.id} style={styles.tr}>
                      <td style={styles.td}>{e.time}</td>
                      <td style={styles.td}>
                        <span style={styles.gapBadge}>{parseFloat(e.gapMinutes || 0).toFixed(2)}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.badgeStyle,
                          background: e.category === 'Planned'
                            ? 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)'
                            : 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                        }}>{e.category}</span>
                      </td>
                      <td style={styles.td}>{e.reason}</td>
                      <td style={styles.td}>
                        <button onClick={() => deleteEntry(e.id)} style={styles.deleteButton}>🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {downtimeEntries.length === 0 && productionId && !loading.loadingEntries && (
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>📋</span>
              <p style={styles.emptyText}>No downtime entries added yet</p>
            </div>
          )}

          {downtimeEntries.length > 0 && productionId && (
            <div style={styles.actionButtons}>
              <button onClick={saveDowntimeEntries} style={styles.saveButton}
                disabled={loading.savingDowntime}>
                {loading.savingDowntime ? '⏳ Saving...' : '💾 Save Downtime Entries'}
              </button>
              <button onClick={resetDowntimeGrid} style={styles.resetButton}
                disabled={loading.savingDowntime}>🔄 Reset Entries</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ───── Styles (all inline — no separate CSS file needed) ─────
const styles = {
  container: { padding: '30px', minHeight: '100vh',
               background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)',
               fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  header: { textAlign: 'center', marginBottom: '30px' },
  title: { fontSize: '36px', fontWeight: '800',
           background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
           WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
           marginBottom: '10px' },
  subtitle: { fontSize: '16px', color: '#64748b', fontWeight: '500' },
  stepBadge: { display: 'inline-block', padding: '2px 8px',
               background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
               color: 'white', fontSize: '12px', fontWeight: '600',
               borderRadius: '12px', margin: '0 5px' },
  formWrapper: { maxWidth: '1400px', margin: '0 auto' },
  section: { background: 'white', padding: '25px', borderRadius: '20px',
             boxShadow: '0 10px 30px rgba(0,0,0,0.08)', marginBottom: '20px',
             border: '1px solid rgba(0,0,0,0.05)' },
  sectionTitle: { fontSize: '22px', fontWeight: '700', color: '#1e293b',
                  marginBottom: '20px', display: 'flex', alignItems: 'center',
                  gap: '12px', paddingBottom: '12px',
                  borderBottom: '3px solid',
                  borderImage: 'linear-gradient(90deg, #2563eb, #7c3aed) 1',
                  flexWrap: 'wrap' },
  sectionIcon: { fontSize: '28px' },
  badge: { padding: '6px 16px',
           background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
           color: 'white', fontSize: '12px', fontWeight: '600', borderRadius: '20px' },
  savedBadge: { marginLeft: 'auto', padding: '6px 16px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white', fontSize: '12px', fontWeight: '600', borderRadius: '20px' },
  checkingBadge: { padding: '6px 16px',
                   background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                   color: 'white', fontSize: '12px', fontWeight: '600', borderRadius: '20px' },
  // ✅ Replaces the .dtf-grid5 CSS class — inline responsive grid via JS media logic isn't
  // possible with plain style objects, so this uses CSS grid with auto-fit which naturally
  // reflows on smaller screens without needing a separate stylesheet or @media block.
  grid5: { display: 'grid',
           gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
           gap: '15px', marginBottom: '20px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '14px', fontWeight: '600', color: '#475569' },
  required: { color: '#ef4444', marginLeft: '4px' },
  input: { padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '10px',
           fontSize: '14px', fontWeight: '500', outline: 'none', background: 'white' },
  select: { padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '10px',
            fontSize: '14px', fontWeight: '500', outline: 'none',
            background: 'white', cursor: 'pointer' },
  successBanner: { padding: '12px 20px',
                   background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                   color: 'white', borderRadius: '10px', marginBottom: '15px',
                   fontWeight: '500', textAlign: 'center', fontSize: '14px' },
  warningBanner: { padding: '16px 20px',
                   background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                   color: 'white', borderRadius: '12px', marginBottom: '15px',
                   fontWeight: '600', textAlign: 'center', fontSize: '14px' },
  loadingBanner: { padding: '12px 20px',
                   background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                   color: 'white', borderRadius: '10px', marginBottom: '15px',
                   fontWeight: '600', textAlign: 'center', fontSize: '14px' },
  infoBanner: { padding: '12px 20px',
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                color: 'white', borderRadius: '10px', marginBottom: '15px',
                fontWeight: '600', textAlign: 'center', fontSize: '14px' },
  actionButtons: { display: 'flex', gap: '15px', marginTop: '25px',
                   justifyContent: 'center', flexWrap: 'wrap' },
  saveButton: { padding: '14px 32px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white', border: 'none', borderRadius: '12px',
                fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  resetButton: { padding: '14px 32px',
                 background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                 color: 'white', border: 'none', borderRadius: '12px',
                 fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  entryForm: { background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
               padding: '20px', borderRadius: '15px', marginBottom: '20px',
               border: '2px solid #e2e8f0' },
  entryGrid: { display: 'grid',
               gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
               gap: '12px', alignItems: 'end' },
  buttonWrapper: { display: 'flex', alignItems: 'flex-end' },
  addButton: { padding: '12px 24px',
               background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
               color: 'white', border: 'none', borderRadius: '10px',
               fontSize: '14px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  tableContainer: { overflowX: 'auto', marginTop: '20px', borderRadius: '12px' },
  tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                 padding: '12px 16px',
                 background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                 borderBottom: '2px solid #e2e8f0', borderRadius: '12px 12px 0 0',
                 flexWrap: 'wrap', gap: '10px' },
  tableHeaderText: { fontSize: '14px', fontWeight: '600', color: '#475569' },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0 },
  th: { background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white', padding: '16px 12px', textAlign: 'left',
        fontWeight: '700', fontSize: '13px', textTransform: 'uppercase' },
  tr: { background: 'white' },
  td: { padding: '16px 12px', borderBottom: '1px solid #e2e8f0',
        color: '#1e293b', fontWeight: '500', fontSize: '14px' },
  gapBadge: { padding: '6px 12px', borderRadius: '8px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white', fontSize: '12px', fontWeight: '600' },
  badgeStyle: { padding: '6px 12px', borderRadius: '8px',
                color: 'white', fontSize: '12px', fontWeight: '600' },
  deleteButton: { padding: '8px 16px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white', border: 'none', borderRadius: '8px',
                  fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '60px 20px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: '15px', border: '2px dashed #cbd5e1' },
  emptyIcon: { fontSize: '48px', display: 'block', marginBottom: '15px' },
  emptyText: { color: '#64748b', fontSize: '16px', fontWeight: '600', margin: '0 0 8px 0' },
};

export default DowntimeForm;